import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api, { extractError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, LoadingState } from "@/components/Common";
import { TaskDialog, TaskCompletableList } from "@/components/CaseTaskDialogs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CheckSquare, PlusCircle, Search, Download, FileSpreadsheet, FileText } from "lucide-react";
import { VoiceSearchButton, appendSearch } from "@/components/VoiceSearchButton";
import { toast } from "sonner";

const VALID_FILTERS = ["tutte", "mie", "oggi", "scadute"];

const Tasks = () => {
  const { can, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilter = VALID_FILTERS.includes(searchParams.get("filtro")) ? searchParams.get("filtro") : "tutte";

  // Enti associati all'utente corrente (per l'opzione "I miei enti")
  const myEnti = useMemo(() => {
    const s = new Set([...(user?.enti_ids || []), ...(user?.ente_id ? [user.ente_id] : [])]);
    return [...s];
  }, [user]);

  // Default: se l'utente ha enti associati e non arriva da un deep-link (parametro filtro),
  // apre già filtrato su "I miei enti".
  const initialEnte = !searchParams.get("filtro") && myEnti.length > 0 ? "__mine__" : "";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(initialFilter);
  const [q, setQ] = useState("");
  const [ente, setEnte] = useState(initialEnte);
  const [entities, setEntities] = useState([]);
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { api.get("/entities").then(({ data }) => setEntities(data)).catch(() => {}); }, []);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    const params = new URLSearchParams();
    if (filter === "mie") params.set("mine", "true");
    if (filter === "oggi") params.set("oggi", "true");
    if (filter === "scadute") params.set("scadute", "true");
    try { const { data } = await api.get(`/tasks?${params.toString()}`); setItems(data); } catch (e) { console.error("Errore caricamento attività", e); }
    finally { if (!silent) setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const changeFilter = (v) => {
    setFilter(v);
    setSearchParams(v === "tutte" ? {} : { filtro: v }, { replace: true });
  };

  // Ricerca per nominativo, numero pratica o titolo attività + filtro per ente
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const mySet = new Set(myEnti);
    return items.filter((t) => {
      if (ente === "__mine__") { if (!t.ente_id || !mySet.has(t.ente_id)) return false; }
      else if (ente && t.ente_id !== ente) return false;
      if (!s) return true;
      return (
        (t.persona_nome || "").toLowerCase().includes(s) ||
        (t.numero_pratica || "").toLowerCase().includes(s) ||
        (t.assegnatario_nome || "").toLowerCase().includes(s) ||
        (t.titolo || "").toLowerCase().includes(s));
    });
  }, [items, q, ente, myEnti]);

  const exportData = async (formato) => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filter === "mie") params.set("mine", "true");
      if (filter === "oggi") params.set("oggi", "true");
      if (filter === "scadute") params.set("scadute", "true");
      if (ente === "__mine__") { if (myEnti.length) params.set("ente_ids", myEnti.join(",")); }
      else if (ente) params.set("ente_id", ente);
      params.set("formato", formato);
      const r = await api.get(`/tasks/export?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = formato === "xlsx" ? "attivita.xlsx" : "attivita.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Esportazione ${formato.toUpperCase()} generata`);
    } catch (e) { toast.error(extractError(e, "Esportazione non riuscita")); }
    finally { setExporting(false); }
  };

  return (
    <div>
      <PageHeader icon={CheckSquare} title="Attività" description="Task, promemoria e follow-up"
        actions={
          <div className="flex items-center gap-2">
            {can("reports.export") && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" disabled={exporting || filtered.length === 0} data-testid="tasks-export-button">
                    <Download className="h-4 w-4 mr-2" /> Esporta
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => exportData("xlsx")} data-testid="tasks-export-xlsx">
                    <FileSpreadsheet className="h-4 w-4 mr-2 text-[color:var(--brand-green-700)]" /> Excel (.xlsx)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportData("csv")} data-testid="tasks-export-csv">
                    <FileText className="h-4 w-4 mr-2 text-[color:var(--brand-blue-600)]" /> CSV (.csv)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {can("tasks.write") && <Button onClick={() => setOpen(true)} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white" data-testid="new-task-button"><PlusCircle className="h-4 w-4 mr-2" /> Nuova attività</Button>}
          </div>
        } />

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <Tabs value={filter} onValueChange={changeFilter}>
          <TabsList><TabsTrigger value="tutte">Tutte</TabsTrigger><TabsTrigger value="mie">Le mie</TabsTrigger><TabsTrigger value="oggi">Oggi</TabsTrigger><TabsTrigger value="scadute">Scadute</TabsTrigger></TabsList>
        </Tabs>
        <Select value={ente || "tutti"} onValueChange={(v) => setEnte(v === "tutti" ? "" : v)}>
          <SelectTrigger className="w-[200px]" data-testid="tasks-filter-ente"><SelectValue placeholder="Ente" /></SelectTrigger>
          <SelectContent className="max-h-64">
            <SelectItem value="tutti">Tutti gli enti</SelectItem>
            {myEnti.length > 0 && <SelectItem value="__mine__" data-testid="tasks-filter-ente-mine">I miei enti</SelectItem>}
            {entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.denominazione}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 sm:max-w-sm sm:ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nominativo, numero pratica o gestore…" className="pl-9 pr-11" data-testid="tasks-search" />
          <div className="absolute right-2 top-1/2 -translate-y-1/2"><VoiceSearchButton onResult={(t) => setQ((p) => appendSearch(p, t))} data-testid="tasks-search-mic" /></div>
        </div>
      </div>

      {loading ? <LoadingState /> : (
        <TaskCompletableList tasks={filtered} canWrite={can("tasks.write")} onChanged={() => load(true)} showPerson />
      )}
      <TaskDialog open={open} setOpen={setOpen} onDone={load} />
    </div>
  );
};

export default Tasks;
